import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  MenuItem,
  LinearProgress,
  Collapse,
  Paper,
  Stack,
  IconButton,
} from "@mui/material";
import {
  MenuBook as MenuBookIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useGetBooksQuery,
  useCreateBookMutation,
} from "@/store/api/adminApi";

// Validation Schema
const validationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  description: Yup.string(),
  book: Yup.string(),
  isPublic: Yup.boolean().default(false),
  allowGuests: Yup.boolean().default(false),
});

const bookOptionLabel = (book) =>
  book?.year ? `${book.title} (${book.year})` : book?.title || "Untitled";

/**
 * Inline "Book / Collection" section, rendered ONLY when "Allow guest" is on.
 * Lets the admin pick an existing book or create a new one (title / description
 * / year) without leaving the exam form.
 */
const BookSection = ({ value, setFieldValue }) => {
  const [expanded, setExpanded] = useState(Boolean(value));
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ title: "", description: "", year: "" });

  const { data: booksData, isLoading: booksLoading } = useGetBooksQuery({
    page: 1,
    limit: 100,
  });
  const bookOptions = booksData?.items || [];

  const [createBook, { isLoading: isCreating }] = useCreateBookMutation();

  const handleCreate = async () => {
    const title = draft.title.trim();
    if (!title) {
      toast.error("Book title is required.");
      return;
    }
    try {
      const payload = { title };
      if (draft.description.trim())
        payload.description = draft.description.trim();
      if (String(draft.year).trim() !== "") payload.year = Number(draft.year);

      const newBook = await createBook(payload).unwrap();
      if (newBook?._id) {
        setFieldValue("book", newBook._id);
      }
      setDraft({ title: "", description: "", year: "" });
      setShowCreate(false);
      toast.success("Book created.");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to create book.");
    }
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          cursor: "pointer",
          bgcolor: "grey.50",
        }}
      >
        <MenuBookIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Book / Collection
        </Typography>
        <ExpandMoreIcon
          sx={{
            transition: "transform 0.2s",
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        />
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Assign to a collection"
            value={value || ""}
            onChange={(e) => setFieldValue("book", e.target.value)}
            disabled={booksLoading}
            helperText="Optional — group this exam under a collection (e.g. ETS 2019)."
          >
            <MenuItem value="">
              <em>— None —</em>
            </MenuItem>
            {bookOptions.map((book) => (
              <MenuItem key={book._id} value={book._id}>
                {bookOptionLabel(book)}
              </MenuItem>
            ))}
          </TextField>

          {!showCreate ? (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowCreate(true)}
              sx={{ mt: 1 }}
            >
              Create new book
            </Button>
          ) : (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "grey.50",
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ flexGrow: 1, fontWeight: 700 }}
                >
                  New book
                </Typography>
                <IconButton size="small" onClick={() => setShowCreate(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Title"
                  required
                  value={draft.title}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, title: e.target.value }))
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  multiline
                  rows={2}
                  value={draft.description}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, description: e.target.value }))
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Year"
                  type="number"
                  value={draft.year}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, year: e.target.value }))
                  }
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreate}
                    disabled={isCreating}
                  >
                    {isCreating ? "Creating…" : "Create"}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const ExamForm = ({
  open,
  onClose,
  onSubmit,
  formData: initialFormData,
  isSubmitting,
  isEditing,
}) => {
  // Get progress from the Redux store
  const uploadProgress = useSelector((state) => state.upload.progress);

  // Initial form values
  const initialValues = {
    title: initialFormData.title || "",
    description: initialFormData.description || "",
    book: initialFormData.book || "",
    isPublic: initialFormData.isPublic || false,
    allowGuests: initialFormData.allowGuests || false,
  };

  // Handle form submission
  const handleSubmit = (values, { setSubmitting }) => {
    if (typeof onSubmit === "function") {
      onSubmit(values);
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting: formikIsSubmitting,
        }) => (
          <Form onSubmit={handleSubmit}>
            <DialogTitle>
              {isEditing ? "Edit Exam" : "Create New Exam"}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="title"
                    label="Title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && !!errors.title}
                    helperText={touched.title && errors.title}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="description"
                    label="Description"
                    multiline
                    rows={3}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Field
                        as={Switch}
                        type="checkbox"
                        name="isPublic"
                        checked={values.isPublic}
                        onChange={handleChange}
                      />
                    }
                    label="Public"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.allowGuests}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setFieldValue("allowGuests", next);
                          // Book grouping only applies to guest-accessible
                          // tests — clear it when guests are disabled.
                          if (!next) setFieldValue("book", "");
                        }}
                      />
                    }
                    label="Allow guests (public mock test)"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", ml: 6, mt: -0.5 }}
                  >
                    Guests can take this test without logging in; their results
                    are not saved. Independent from "Public".
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ pt: 0 }}>
                  <Collapse in={values.allowGuests}>
                    {values.allowGuests && (
                      <BookSection
                        value={values.book}
                        setFieldValue={setFieldValue}
                      />
                    )}
                  </Collapse>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{ position: "relative", pt: isSubmitting ? 3 : 1 }}
            >
              {isSubmitting && (
                <LinearProgress
                  variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
                  value={uploadProgress}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }}
                />
              )}
              <Button type="button" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || formikIsSubmitting}
              >
                {isEditing ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ExamForm;
